import type { Role } from "./tenancy.js";

/**
 * The permission matrix: role x resource x action, with an explicit scope.
 *
 * Kept as data rather than scattered `if (role === ...)` checks so it can be
 * reviewed against the scheme's requirements in one place, rendered in the org
 * admin UI, and tested exhaustively.
 */

export type Resource =
  | "lead"
  | "contact"
  | "job"
  | "quote"
  | "survey"
  | "design"
  | "document"
  | "signature"
  /**
   * Geo-tagged photographic evidence. Its own resource rather than a kind of
   * `document`, because the people who take it are not the people who produce
   * documents: PAS 2030 clause 6.11 covered-up-work photographs are an
   * installer's job and PAS 2035 clause 7.4.1 assessment photographs are a
   * surveyor's, and a surveyor holds no `document: create` at all. Folding the
   * two together would either have refused the surveyor their own evidence or
   * handed them document authorship they are deliberately not given.
   */
  | "photo-evidence"
  | "chat"
  | "task"
  | "member"
  | "org-settings"
  | "evidence-vault"
  | "billing";

export type Action =
  | "read"
  | "create"
  | "update"
  | "delete"
  | "sign-off" // technical sign-off of a design or installation
  | "issue" // fix a document as final: hashed, immutable, retained as evidence
  | "send" // email a document to a recipient
  | "share" // share a job with another organisation
  /**
   * Remove a job with nothing in it: no photograph, scan, drawing, rooms or
   * record in its own name. Its own action rather than `delete`, because that
   * is the point: a surveyor opens jobs on a phone and should be able to take
   * back one that came to nothing, and holding this gives them no `delete` on
   * a job or a survey. The server decides what counts as blank
   * (`crm/job-blank-delete.ts`), and evidence stays undeletable by anybody.
   */
  | "delete-blank";

/**
 * "own" limits the permission to records the user is assigned to (a surveyor's
 * own bookings, an installer's own jobs). "org" covers everything in the
 * organisation. "none" means no access.
 *
 * An "own" grant is a standing obligation on the calling service: it must read
 * the real assignment out of the database and pass it as `isAssigned`. A caller
 * that omits it is refused, because a scope nothing resolves would otherwise
 * read as a control while granting the whole organisation.
 */
export type Scope = "none" | "own" | "org";

type ResourceGrants = Partial<Record<Action, Scope>>;
type RoleGrants = Partial<Record<Resource, ResourceGrants>>;

const ALL: ResourceGrants = {
  read: "org",
  create: "org",
  update: "org",
  delete: "org",
  issue: "org",
  send: "org",
  share: "org",
};

const READ_ONLY_ORG: ResourceGrants = { read: "org" };

/**
 * Photographic evidence at organisation scope — with no `delete`.
 *
 * Not an oversight and not `ALL` minus a line. Once the wall is closed a clause
 * 6.11 photograph cannot be retaken at any price, so there is no role, owner
 * included, that may remove one. Retention is a clock that runs to the guarantee
 * period, which on external wall insulation is routinely twenty-five years; it
 * is not a button somebody presses to tidy up.
 */
const EVIDENCE_CAPTURE_ORG: ResourceGrants = {
  read: "org",
  create: "org",
  update: "org",
};

export const PERMISSION_MATRIX: Record<Role, RoleGrants> = {
  owner: {
    lead: ALL,
    contact: ALL,
    job: { ...ALL, "delete-blank": "org" },
    quote: ALL,
    survey: ALL,
    design: ALL,
    document: ALL,
    signature: ALL,
    chat: ALL,
    task: ALL,
    member: ALL,
    "org-settings": ALL,
    "evidence-vault": { read: "org" },
    /**
     * THE ONE ROLE THAT MAY DELETE A PHOTOGRAPH, and only into the recycle
     * bin, where it is recoverable for thirty days before the purge.
     *
     * `EVIDENCE_CAPTURE_ORG` still says what it said for every other role:
     * a clause 6.11 photograph is not theirs to remove. This grant is the
     * owner's alone, asked for deliberately, and the cost is real -- once
     * a wall is closed the photograph cannot be retaken, and its retention
     * minimum runs to six, ten or twenty-five years depending on the
     * measure. The screen names that date before it does anything, and the
     * audit log keeps the record of who deleted what, permanently.
     */
    "photo-evidence": { ...EVIDENCE_CAPTURE_ORG, delete: "org" },
    billing: ALL,
  },

  admin: {
    lead: ALL,
    contact: ALL,
    job: { ...ALL, "delete-blank": "org" },
    quote: ALL,
    survey: ALL,
    design: ALL,
    document: ALL,
    signature: ALL,
    chat: ALL,
    task: ALL,
    member: ALL,
    "org-settings": ALL,
    "evidence-vault": { read: "org" },
    "photo-evidence": EVIDENCE_CAPTURE_ORG,
    billing: READ_ONLY_ORG,
  },

  // Sales and administration: runs the pipeline and the paperwork, but has no
  // technical authority — cannot sign off a design.
  office: {
    lead: ALL,
    contact: ALL,
    job: { read: "org", create: "org", update: "org" },
    quote: ALL,
    survey: READ_ONLY_ORG,
    design: READ_ONLY_ORG,
    document: { read: "org", create: "org", update: "org", issue: "org", send: "org" },
    signature: { read: "org", create: "org" },
    chat: { read: "org", create: "org" },
    task: ALL,
    "evidence-vault": { read: "org" },
    // Office corrects a caption; it never captures. The measured fix, the
    // bearing and the timestamps are refused an update path by the database, so
    // "update" here reaches the description and nothing else.
    "photo-evidence": { read: "org", update: "org" },
  },

  // Field role. Sees only assigned work, and deliberately has no access to
  // quotes or pricing.
  surveyor: {
    // "create" at "own": the field app opens a job for the property being
    // surveyed (`POST /api/jobs`) before it sends the survey, and the job is
    // booked to the surveyor who opened it (`createJob`) — so the survey,
    // scan and photographs that follow land on a job of their own. They still
    // see no job they are not booked onto.
    // "delete-blank" at "own": a job booked to them that came to nothing.
    job: { read: "own", create: "own", update: "own", "delete-blank": "own" },
    contact: { read: "own" },
    survey: { read: "own", create: "own", update: "own" },
    design: { read: "own" },
    document: { read: "own" },
    // Captures the customer's signature on site (commissioning/handover).
    signature: { read: "own", create: "own" },
    // PAS 2035 clause 7.4.1: a photographic record of every recorded feature and
    // identified defect in the retrofit assessment.
    "photo-evidence": { read: "own", create: "own", update: "own" },
    chat: { read: "org", create: "org" },
    task: { read: "own", update: "own" },
  },

  // Produces designs but cannot approve them — separation of duties mirroring
  // the scheme's Technical Supervisor requirement.
  designer: {
    job: { read: "org" },
    contact: { read: "org" },
    survey: { read: "org", update: "org" },
    design: { read: "org", create: "org", update: "org" },
    // May finalise the documents they produce. Issuing is publication, not
    // technical approval — signing off a design remains the Technical
    // Supervisor's alone, which is the separation the scheme actually requires.
    document: { read: "org", create: "org", issue: "org" },
    chat: { read: "org", create: "org" },
    task: { read: "org", update: "org" },
    "evidence-vault": { read: "org" },
    "photo-evidence": READ_ONLY_ORG,
  },

  // IOR §5.5: the only role that can sign off the technical compliance of a
  // design or an installation. Recorded on every MCS certificate.
  "technical-supervisor": {
    job: { read: "org", update: "org" },
    contact: { read: "org" },
    survey: { read: "org", update: "org" },
    design: { read: "org", create: "org", update: "org", "sign-off": "org" },
    document: { read: "org", create: "org", issue: "org", send: "org", "sign-off": "org" },
    signature: { read: "org", create: "org" },
    chat: { read: "org", create: "org" },
    task: { read: "org", update: "org" },
    "evidence-vault": { read: "org" },
    "photo-evidence": { read: "org", update: "org" },
  },

  installer: {
    job: { read: "own", update: "own" },
    contact: { read: "own" },
    // Reads the survey of a job they are booked onto — the floor plan with its
    // corrections and dimensions, the scan, the 3D model, the heat loss — and
    // changes none of it: no create, no update. Without this the installer
    // held the plan's design layer (`design: read` below) but could open none
    // of the survey it is drawn on, so the Survey page refused the one person
    // who has to build what it shows. Every survey read path resolves "own"
    // through the job's assigned surveyor OR installer, so this reaches their
    // own jobs and no others.
    survey: { read: "own" },
    design: { read: "own" },
    document: { read: "own", create: "own" },
    signature: { read: "own", create: "own" },
    // The clause 6.11 photographer. This is the largest group of users of this
    // feature and the reason it exists.
    "photo-evidence": { read: "own", create: "own", update: "own" },
    chat: { read: "org", create: "org" },
    task: { read: "own", update: "own" },
  },

  // For a certification-body assessor at audit time: read everything that
  // constitutes the evidence, change nothing.
  auditor: {
    job: READ_ONLY_ORG,
    survey: READ_ONLY_ORG,
    design: READ_ONLY_ORG,
    document: READ_ONLY_ORG,
    signature: READ_ONLY_ORG,
    "evidence-vault": READ_ONLY_ORG,
    "photo-evidence": READ_ONLY_ORG,
  },
};

export interface AccessRequest {
  readonly role: Role;
  readonly resource: Resource;
  readonly action: Action;
  /** True when the actor is assigned to the specific record in question. */
  readonly isAssigned?: boolean;
}

/** Resolves the scope a role holds for an action, defaulting to no access. */
export function scopeFor(
  role: Role,
  resource: Resource,
  action: Action,
): Scope {
  return PERMISSION_MATRIX[role][resource]?.[action] ?? "none";
}

/**
 * The single authorisation decision point. Deny by default: anything not
 * explicitly granted in the matrix is refused.
 */
export function can(request: AccessRequest): boolean {
  const scope = scopeFor(request.role, request.resource, request.action);
  switch (scope) {
    case "none":
      return false;
    case "org":
      return true;
    case "own":
      return request.isAssigned === true;
  }
}

/** How much a scope grants, for comparing one role's reach against another's. */
const SCOPE_RANK: Record<Scope, number> = { none: 0, own: 1, org: 2 };

/**
 * Every (resource, action) pair the matrix grants to anybody, derived from the
 * matrix rather than listed again. A hand-written second list is how a resource
 * added next year quietly stops being compared — and the comparison failing
 * silently means a demotion that leaves the old sessions alive.
 */
const GRANTED_PAIRS: readonly (readonly [Resource, Action])[] = (() => {
  const pairs = new Set<string>();
  for (const grants of Object.values(PERMISSION_MATRIX)) {
    for (const [resource, actions] of Object.entries(grants)) {
      for (const action of Object.keys(actions ?? {})) {
        pairs.add(`${resource} ${action}`);
      }
    }
  }
  return [...pairs].map((pair) => {
    const [resource, action] = pair.split(" ");
    return [resource as Resource, action as Action] as const;
  });
})();

/**
 * True when moving from `from` to `to` takes something away.
 *
 * Asked when a role is changed, because the two directions deserve different
 * treatment: widening somebody's duties is ordinary administration, and
 * narrowing them is a withdrawal of trust that should end the sessions they
 * hold — the same reasoning as deactivation, one notch down.
 *
 * Derived from the matrix rather than from a hand-written ordering of the eight
 * roles, because there is no such ordering. A designer is not "more" or "less"
 * than a surveyor; each holds things the other does not. So the question is
 * asked per permission: if ANY (resource, action) pair is worth less under the
 * new role than the old, something was taken away. A sideways move that gives
 * and takes counts as narrowing, which is the safe way to be wrong about it.
 */
export function narrowsPermissions(from: Role, to: Role): boolean {
  if (from === to) return false;
  for (const [resource, action] of GRANTED_PAIRS) {
    const before = SCOPE_RANK[scopeFor(from, resource, action)];
    const after = SCOPE_RANK[scopeFor(to, resource, action)];
    if (after < before) return true;
  }
  return false;
}

/** Every role that can perform an action, for rendering "who can do this" in admin UI. */
export function rolesWith(resource: Resource, action: Action): readonly Role[] {
  return (Object.keys(PERMISSION_MATRIX) as Role[]).filter(
    (role) => scopeFor(role, resource, action) !== "none",
  );
}
